import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { AxiosResponse } from 'axios'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'

import casinoService from '../../services/casino.service'
import authService from '../../services/auth.service'
import LiveCasinoHeader from './LiveCasinoHeader'
import {
  LIVE_CATEGORY_ORDER,
  LIVE_CASINO_PROVIDERS,
  LIVE_CASINO_SEARCH_TERMS,
  LiveCategoryId,
  filterLiveGamesByQuery,
  groupGamesByCategory,
  isLiveCasinoGame,
  mergeUniqueGames,
} from '../../utils/liveCasinoCategories'
import {
  buildCasinoIntLaunchPath,
  buildCasinoOpenPayload,
} from '../../utils/casinoGameLaunch'
import './liveCasino.css'

const DEFAULT_VIS_CONFIG = {
  disabledProviders: [] as string[],
  disabledGames: [] as string[],
  overrideImages: {} as Record<string, string>,
  useCustomImage: {} as Record<string, boolean>,
}

const API_BASE = (process.env.REACT_APP_API_BASEURL || '').replace(/\/api\/?$/, '')

type ViewFilter = 'all' | 'top' | 'live' | 'table' | LiveCategoryId

function resolveImage(
  item: Record<string, unknown>,
  overrideImages?: Record<string, string>,
  useCustomImage?: Record<string, boolean>,
) {
  const gid = String(item.game_identifier || item.identifier || item._id || '')
  if (gid && overrideImages?.[gid] && useCustomImage?.[gid]) {
    const override = overrideImages[gid]
    return override.startsWith('http') ? override : `${API_BASE}${override}`
  }
  const url = item.game_images || item.image_url || item.game_image
  return url && typeof url === 'string' ? url.trim() : ''
}

const LiveCasinoGameCard = ({
  game,
  onPlay,
  overrideImages,
  useCustomImage,
}: {
  game: Record<string, unknown>
  onPlay: (g: Record<string, unknown>) => void
  overrideImages?: Record<string, string>
  useCustomImage?: Record<string, boolean>
}) => {
  const [failed, setFailed] = useState(false)
  const imageUrl = resolveImage(game, overrideImages, useCustomImage)

  return (
    <div
      className='live-casino-game-card'
      onClick={() => onPlay(game)}
      title={String(game.game_name || '')}
    >
      {!failed && imageUrl ? (
        <img
          src={imageUrl}
          alt={String(game.game_name || 'Game')}
          loading='lazy'
          onError={() => setFailed(true)}
        />
      ) : (
        <div className='live-casino-game-card__placeholder'>
          {String(game.game_name || 'Game')}
        </div>
      )}
    </div>
  )
}

/** After-login Live Casino lobby (vajrawin-style). */
const LiveCasinoPage = () => {
  const navigate = useNavigate()
  const [games, setGames] = useState<Record<string, unknown>[]>([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [viewFilter, setViewFilter] = useState<ViewFilter>('all')
  const [categoryFilter, setCategoryFilter] = useState<LiveCategoryId | null>(null)
  const [visibilityConfig, setVisibilityConfig] = useState(DEFAULT_VIS_CONFIG)
  const [showTopBtn, setShowTopBtn] = useState(false)

  const sectionRefs = useRef<Partial<Record<LiveCategoryId, HTMLDivElement | null>>>({})

  useEffect(() => {
    casinoService
      .getCasinoVisibilityConfig()
      .then((res: AxiosResponse<any>) => {
        const data = res.data?.data || res.data || {}
        setVisibilityConfig({
          disabledProviders: data.disabledProviders || [],
          disabledGames: data.disabledGames || [],
          overrideImages: data.overrideImages || {},
          useCustomImage: data.useCustomImage || {},
        })
      })
      .catch(() => setVisibilityConfig(DEFAULT_VIS_CONFIG))
  }, [])

  useEffect(() => {
    let cancelled = false
    setLoading(true)

    const searchPromises = LIVE_CASINO_SEARCH_TERMS.map((term) =>
      casinoService.searchCasinoGames(term).then((res) => res.data?.result || []),
    )
    const providerPromises = LIVE_CASINO_PROVIDERS.flatMap((provider) => [
      casinoService.getIntCasinoList(provider, 'live-casino').then((res) => res.data?.result || []),
      casinoService.getIntCasinoList(provider, 'slots').then((res) => res.data?.result || []),
    ])

    Promise.all([...searchPromises, ...providerPromises])
      .then((results) => {
        if (cancelled) return
        const merged = mergeUniqueGames(results).filter(isLiveCasinoGame)
        setGames(merged)
      })
      .catch(() => {
        if (!cancelled) setGames([])
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [])

  const visibleGames = useMemo(() => {
    let list = games

    if (visibilityConfig.disabledProviders.length) {
      list = list.filter((g) => {
        const pid = String(g.provider_id || g.provider || g.game_provider || '')
        return !pid || !visibilityConfig.disabledProviders.includes(pid)
      })
    }

    if (visibilityConfig.disabledGames.length) {
      list = list.filter((g) => {
        const gid = String(g.game_identifier || g.identifier || g._id || '')
        return !gid || !visibilityConfig.disabledGames.includes(gid)
      })
    }

    return filterLiveGamesByQuery(list, searchQuery)
  }, [games, visibilityConfig, searchQuery])

  const grouped = useMemo(() => groupGamesByCategory(visibleGames), [visibleGames])

  const topGames = useMemo(() => visibleGames.slice(0, 24), [visibleGames])

  const tableGames = useMemo(() => {
    const ids = new Set<string>()
    const result: Record<string, unknown>[] = []
    for (const id of [
      'baccarat',
      'blackjack',
      'roulette',
      'teenpatti',
      'poker',
      'table',
    ] as LiveCategoryId[]) {
      for (const g of grouped[id]) {
        const key = String(g._id || g.game_identifier || '')
        if (key && !ids.has(key)) {
          ids.add(key)
          result.push(g)
        }
      }
    }
    return result
  }, [grouped])

  /**
   * Same open flow as frontend Home "Lobby GAMES":
   * navigate → /casino-int/{type}?category={provider} + state.openGame
   * → casinoOther getplaycasino → iframe.
   *
   * Lobby tiles: pick a real game for that provider to bootstrap the session.
   * Evolution also sets openProviderLobby so casinoOther jumps to the lobby SPA.
   */
  const launchGame = useCallback(
    async (game: Record<string, unknown>) => {
      if (!authService.isLoggedIn()) {
        toast.error('Please login to play')
        return
      }

      const openLikeHome = (source: Record<string, unknown>) => {
        const payload = buildCasinoOpenPayload(source)
        const path = buildCasinoIntLaunchPath(source)
        if (!payload || !path) {
          toast.error('Unable to open this game. Please try again.')
          return
        }
        // Same as dogexch Home Lobby GAMES → casinoOther auto-launch
        navigate(path, {
          state: {
            openGame: payload,
            playGame: payload,
          },
        })
      }

      // LOBBY section: launch the real HUIDU lobby game_uid (same as dogexch Lobby GAMES).
      // Do NOT pick a random table for the provider — that opens a game, not the lobby.
      if (game.isLobby) {
        const identifier = String(game.identifier || game.game_identifier || '').trim()
        if (!identifier) {
          toast.error('Lobby game is not configured')
          return
        }
        openLikeHome({
          _id: identifier,
          identifier,
          game_identifier: identifier,
          game_name: game.game_name,
          game_provider: game.game_provider,
          game_images: game.game_images || game.game_image,
          game_slot_status: true,
        })
        return
      }

      openLikeHome(game)
    },
    [navigate, visibleGames],
  )

  useEffect(() => {
    const handleScroll = () => {
      const scrollContainer = document.querySelector('.desktop-layout-wrapper')
      const currentScroll = scrollContainer
        ? (scrollContainer as HTMLElement).scrollTop
        : window.scrollY
      setShowTopBtn(currentScroll > 300)
    }

    const scrollContainer = document.querySelector('.desktop-layout-wrapper')
    const target = scrollContainer || window
    target.addEventListener('scroll', handleScroll)
    return () => target.removeEventListener('scroll', handleScroll)
  }, [])

  const scrollToTop = () => {
    const scrollContainer = document.querySelector('.desktop-layout-wrapper')
    if (scrollContainer) {
      scrollContainer.scrollTo({ top: 0, behavior: 'smooth' })
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }

  const scrollToCategory = (catId: LiveCategoryId) => {
    setCategoryFilter(catId)
    setViewFilter('all')
    const el = sectionRefs.current[catId]
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }

  const sectionsToShow = useMemo(() => {
    if (viewFilter === 'top') {
      return [{ id: 'top' as const, label: 'TOP GAMES', games: topGames }]
    }
    if (viewFilter === 'live') {
      return [{ id: 'live' as LiveCategoryId, label: 'LIVE GAMES', games: grouped.live }]
    }
    if (viewFilter === 'table') {
      return [{ id: 'table' as LiveCategoryId, label: 'TABLE GAMES', games: tableGames }]
    }
    if (categoryFilter) {
      const cat = LIVE_CATEGORY_ORDER.find((c) => c.id === categoryFilter)
      if (cat) {
        return [{ id: cat.id, label: cat.label, games: grouped[cat.id] || [] }]
      }
    }

    // LOBBY first, then other categories that have games
    return LIVE_CATEGORY_ORDER.map((cat) => ({
      id: cat.id,
      label: cat.label,
      games: grouped[cat.id] || [],
    })).filter((s) => s.id === 'lobby' || s.games.length > 0)
  }, [viewFilter, categoryFilter, grouped, topGames, tableGames])

  return (
    <div className='casino-page-wrap live-casino-page'>
      <LiveCasinoHeader />

      <div className='live-casino-content'>
        <div className='live-casino-filters'>
          <div className='live-casino-filter-row live-casino-filter-row--primary'>
            {(
              [
                { id: 'all' as ViewFilter, label: 'ALL GAMES' },
                { id: 'top' as ViewFilter, label: 'TOP GAMES' },
                { id: 'live' as ViewFilter, label: 'LIVE GAMES' },
                { id: 'table' as ViewFilter, label: 'TABLE GAMES' },
              ] as const
            ).map((f) => (
              <button
                key={f.id}
                type='button'
                className={`live-casino-filter-btn live-casino-filter-btn--liquid ${
                  viewFilter === f.id && !categoryFilter ? 'active' : ''
                }`}
                onClick={() => {
                  setViewFilter(f.id)
                  setCategoryFilter(null)
                }}
              >
                <span>{f.label}</span>
                <div className='liquid' />
              </button>
            ))}
            <div className='live-casino-search'>
              <span className='live-casino-search__icon'>🔍</span>
              <input
                className='live-casino-search__input'
                placeholder='SEARCH GAMES'
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                autoComplete='off'
              />
            </div>
          </div>

          <div className='live-casino-filter-row'>
            {LIVE_CATEGORY_ORDER.map((cat) => (
              <button
                key={cat.id}
                type='button'
                className={`live-casino-filter-btn ${
                  categoryFilter === cat.id ? 'active' : ''
                }`}
                onClick={() => scrollToCategory(cat.id)}
              >
                {cat.filterLabel}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className='live-casino-loading'>
            <div className='lds-hourglass' />
            <p>Loading live casino games...</p>
          </div>
        ) : sectionsToShow.length === 0 ? (
          <div className='live-casino-empty'>
            <p>No live casino games found.</p>
          </div>
        ) : (
          sectionsToShow.map((section) => (
            <div
              key={String(section.id)}
              className='live-casino-section'
              ref={(el) => {
                if (section.id !== 'top') {
                  sectionRefs.current[section.id as LiveCategoryId] = el
                }
              }}
            >
              <div className='live-casino-section__title'>
                <div className='live-casino-section__title-name'>{section.label}</div>
              </div>
              <div className='live-casino-section__scroll custom-gaming-scrollbar'>
                <div className='live-casino-section__items'>
                  {section.games.map((game, idx) => (
                    <LiveCasinoGameCard
                      key={String(game._id || game.game_identifier || idx)}
                      game={game}
                      onPlay={launchGame}
                      overrideImages={visibilityConfig.overrideImages}
                      useCustomImage={visibilityConfig.useCustomImage}
                    />
                  ))}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {showTopBtn && (
        <button
          onClick={scrollToTop}
          style={{
            position: 'fixed',
            bottom: '40px',
            right: '25px',
            height: '45px',
            width: '45px',
            borderRadius: '50%',
            backgroundColor: '#0F2326',
            color: '#fff',
            border: 'none',
            cursor: 'pointer',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 4px 10px rgba(0,0,0,0.3)',
          }}
          title='Scroll to Top'
          type='button'
        >
          <svg
            xmlns='http://www.w3.org/2000/svg'
            width='24'
            height='24'
            viewBox='0 0 24 24'
            fill='none'
            stroke='currentColor'
            strokeWidth='2'
          >
            <path d='M12 19V5M5 12l7-7 7 7' />
          </svg>
        </button>
      )}
    </div>
  )
}

export default LiveCasinoPage
